import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Hash, Lock } from 'lucide-react';
import { api, errMsg } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { initialsOf } from '@/components/contacts/ContactCard';

export const MemberRow = ({ m, right, onClick, testid }) => (
  <div role={onClick ? 'button' : undefined} onClick={onClick} className={`flex items-center gap-2.5 rounded-xl px-2 py-1.5 ${onClick ? 'cursor-pointer hover:bg-muted' : ''}`} data-testid={testid}>
    <Avatar className="h-7 w-7"><AvatarFallback className="bg-primary text-primary-foreground text-[10px] font-semibold">{initialsOf(m.full_name || m.email)}</AvatarFallback></Avatar>
    <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{m.full_name || m.email}</div><div className="text-[11px] text-muted-foreground truncate">{m.email}</div></div>
    {right}
  </div>
);

export const NewChannelDialog = ({ companyId, open, onClose, onCreated, members, me, lang }) => {
  const [form, setForm] = useState({ name: '', description: '', kind: 'channel', member_ids: [] });
  const [busy, setBusy] = useState(false);
  const id = lang === 'id';
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/discuss/channels`, form);
      toast.success(id ? 'Kanal dibuat' : 'Channel created');
      setForm({ name: '', description: '', kind: 'channel', member_ids: [] });
      onCreated(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const toggleMember = (uid) => setForm((f) => ({ ...f, member_ids: f.member_ids.includes(uid) ? f.member_ids.filter((x) => x !== uid) : [...f.member_ids, uid] }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="new-channel-dialog">
        <DialogHeader><DialogTitle className="font-display">{id ? 'Kanal baru' : 'New channel'}</DialogTitle><DialogDescription>{id ? 'Kanal publik dapat dilihat semua anggota perusahaan. Grup privat hanya untuk anggota terpilih.' : 'Public channels are visible to everyone in the company. Private groups are for selected members only.'}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {[['channel', Hash, id ? 'Kanal publik' : 'Public channel'], ['group', Lock, id ? 'Grup privat' : 'Private group']].map(([k, Icon, label]) => (
              <button type="button" key={k} onClick={() => setForm({ ...form, kind: k })} className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors ${form.kind === k ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-muted'}`} data-testid={`new-channel-kind-${k}`}><Icon size={15} />{label}</button>
            ))}
          </div>
          <div className="space-y-1.5"><Label>{id ? 'Nama' : 'Name'}</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={id ? 'mis. marketing' : 'e.g. marketing'} className="rounded-xl" data-testid="new-channel-name" /></div>
          <div className="space-y-1.5"><Label>{id ? 'Deskripsi' : 'Description'}</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={id ? 'Tentang apa kanal ini?' : 'What is this channel about?'} className="rounded-xl" data-testid="new-channel-description" /></div>
          {form.kind === 'group' && (
            <div className="space-y-1.5">
              <Label>{id ? 'Anggota' : 'Members'}</Label>
              <div className="max-h-44 overflow-y-auto rounded-xl border border-border p-1">
                {members.filter((m) => m.user_id !== me.id).map((m) => (
                  <MemberRow key={m.user_id} m={m} onClick={() => toggleMember(m.user_id)} testid={`new-channel-member-${m.user_id}`} right={<Checkbox checked={form.member_ids.includes(m.user_id)} onCheckedChange={() => toggleMember(m.user_id)} />} />
                ))}
                {members.length <= 1 && <p className="px-2 py-2 text-xs text-muted-foreground">{id ? 'Undang pengguna lain di Pengaturan → Pengguna.' : 'Invite other users in Settings → Users.'}</p>}
              </div>
            </div>
          )}
          <DialogFooter><Button type="button" variant="outline" className="rounded-xl" onClick={onClose}>{id ? 'Batal' : 'Cancel'}</Button><Button type="submit" className="rounded-xl" disabled={busy || !form.name.trim()} data-testid="new-channel-submit">{busy ? <Loader2 size={16} className="animate-spin" /> : (id ? 'Buat' : 'Create')}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const NewDmDialog = ({ open, onClose, members, me, onPick, lang }) => {
  const [q, setQ] = useState('');
  const id = lang === 'id';
  const list = members.filter((m) => m.user_id !== me.id && (m.full_name || m.email).toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="new-dm-dialog">
        <DialogHeader><DialogTitle className="font-display">{id ? 'Pesan langsung' : 'Direct message'}</DialogTitle><DialogDescription>{id ? 'Pilih rekan kerja untuk memulai percakapan.' : 'Pick a teammate to start a conversation.'}</DialogDescription></DialogHeader>
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={id ? 'Cari nama...' : 'Search name...'} className="rounded-xl" data-testid="new-dm-search" />
        <div className="max-h-64 overflow-y-auto space-y-0.5">
          {list.map((m) => <MemberRow key={m.user_id} m={m} onClick={() => onPick(m.user_id)} testid={`new-dm-member-${m.user_id}`} />)}
          {list.length === 0 && <p className="px-2 py-4 text-center text-xs text-muted-foreground">{id ? 'Tidak ada pengguna lain. Undang tim Anda di Pengaturan → Pengguna.' : 'No other users. Invite your team in Settings → Users.'}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
